import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FliptProvider, useFliptContext } from '@flipt-io/flipt-client-react';
import { FeatureFlagContext } from './FeatureFlagContext';
const DEFAULT_UPDATE_INTERVAL = 120;

/**
 * No-op fetcher that returns an empty flag snapshot without network calls.
 * Used when `disabled` is true — WASM initializes with zero flags,
 * so all useFliptBoolean/useFliptVariant calls safely return their fallback values.
 *
 * Returns a real Response object (not a duck-typed mock) so that
 * any Flipt SDK method (.text(), .clone(), .headers.entries(), etc.) works safely.
 */
const NOOP_FETCHER = async () => new Response(JSON.stringify({
  namespace: {
    key: ''
  },
  flags: []
}), {
  status: 200,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Polling interval (ms) for the useSyncExternalStore shim workaround.
 * The shim used by @flipt-io/flipt-client-react sometimes misses store
 * notifications, leaving isLoading stuck at true even after init succeeds.
 * This polling detects the mismatch and forces a re-render.
 */
const STORE_SYNC_POLL_MS = 500;

/** Maximum polling attempts before giving up (500ms × 20 = 10s). */
const STORE_SYNC_MAX_ATTEMPTS = 20;
export interface FeatureFlagProviderProps {
  /** Feature flag server URL */
  serverUrl: string;
  /** Flipt namespace for flag scoping */
  namespace: string;
  /** Environment (e.g., 'development', 'live') */
  environment: string;
  /** User-level ID for flag evaluation — user hashId (play) or quizUserId (quiz).
   *  Empty string when not logged in. Combined with deviceId as fallback internally. */
  userId: string;
  /** Device-level ID — stable across login/logout, used as WASM instance key and device bucketing */
  deviceId: string;
  /** Polling interval in seconds (default: 120) */
  updateInterval?: number;
  /** Pre-evaluated boolean flag values from SSR (seed) */
  ssrFlags?: Record<string, boolean>;
  /** Pre-evaluated variant flag values from SSR (seed) */
  ssrVariants?: Record<string, string>;
  /** When true, skips Flipt server fetch entirely (noop fetcher).
   *  WASM initializes with empty flags — all evaluations return fallback values.
   *  Use for contexts where feature flags are unnecessary (e.g., iframe embeds). */
  disabled?: boolean;
  children: React.ReactNode;
}

/**
 * React provider that wraps @flipt-io/flipt-client-react's FliptProvider
 * while preserving our custom interface (ssrFlags, ssrVariants, userId, deviceId, isReady).
 *
 * Props are flat primitives to avoid reference instability from config objects.
 * An inner bridge component reads Flipt's isLoading state once and provides it
 * via our context, preventing N-subscription overhead from multiple hooks.
 *
 * Targeting context (e.g., spaceHashId) is NOT managed here — each domain hook
 * passes its own context via the per-call `context` option in useFeatureFlag.
 */
export function FeatureFlagProvider({
  serverUrl,
  namespace,
  environment,
  userId,
  deviceId,
  updateInterval = DEFAULT_UPDATE_INTERVAL,
  ssrFlags,
  ssrVariants,
  disabled = false,
  children
}: FeatureFlagProviderProps) {
  const userEntityId = userId || deviceId;
  const deviceEntityId = deviceId;
  const fliptOptions = useMemo(() => ({
    namespace,
    url: serverUrl,
    environment,
    updateInterval,
    ...(disabled ? {
      fetcher: NOOP_FETCHER
    } : {})
  }), [namespace, serverUrl, environment, updateInterval, disabled]);

  // Skip FliptProvider when deviceId is empty — avoids meaningless WASM
  // initialization that would be discarded once a real ID arrives.
  if (!deviceEntityId) {
    return <>{children}</>;
  }
  return <FliptProvider key={`${deviceEntityId}-${disabled}`} options={fliptOptions} data-sentry-element="FliptProvider" data-sentry-component="FeatureFlagProvider" data-sentry-source-file="FeatureFlagProvider.tsx">
      <FeatureFlagBridge userEntityId={userEntityId} deviceEntityId={deviceEntityId} ssrFlags={ssrFlags} ssrVariants={ssrVariants} data-sentry-element="FeatureFlagBridge" data-sentry-source-file="FeatureFlagProvider.tsx">
        {children}
      </FeatureFlagBridge>
    </FliptProvider>;
}

/**
 * Inner bridge that reads useFliptContext() exactly once,
 * then exposes isReady via our FeatureFlagContext.
 * This prevents N hooks from each subscribing to Flipt independently.
 *
 * Includes a workaround for a known issue in @flipt-io/flipt-client-react's
 * use of use-sync-external-store/shim: the shim sometimes fails to trigger
 * a React re-render when the Flipt store's isLoading transitions to false.
 * We poll the raw store object to detect this mismatch and force a re-render.
 */
function FeatureFlagBridge({
  userEntityId,
  deviceEntityId,
  ssrFlags,
  ssrVariants,
  children
}: {
  userEntityId: string;
  deviceEntityId: string;
  ssrFlags?: Record<string, boolean>;
  ssrVariants?: Record<string, string>;
  children: React.ReactNode;
}) {
  const fliptCtx = useFliptContext();
  const {
    isLoading
  } = fliptCtx;

  // Workaround: poll the raw store to detect stale useSyncExternalStore state.
  // The fliptCtx object is a mutable ref (storeRef.current from FliptProvider),
  // so reading its properties in setInterval gives the live value even when
  // useSyncExternalStore fails to notify React.
  const [syncFix, setSyncFix] = useState(0);
  const [hasGivenUp, setHasGivenUp] = useState(false);
  const pollCountRef = useRef(0);
  useEffect(() => {
    if (!isLoading) return; // already synced, no need to poll
    if (syncFix > 0) return; // workaround already fired, stop polling
    if (hasGivenUp) return; // already gave up, stop polling

    pollCountRef.current = 0;
    const id = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= STORE_SYNC_MAX_ATTEMPTS) {
        clearInterval(id);
        console.warn(`[@zep/feature-flag] useSyncExternalStore shim polling gave up after ${STORE_SYNC_MAX_ATTEMPTS} attempts. ` + 'Flipt WASM may have failed to initialize. SSR seed values will continue to be used.');
        setHasGivenUp(true);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = fliptCtx as any;
      if (raw.client && raw.isLoading === false) {
        setSyncFix(n => n + 1); // force re-render to pick up new state
      }
    }, STORE_SYNC_POLL_MS);
    return () => clearInterval(id);
  }, [isLoading, fliptCtx, syncFix, hasGivenUp]);

  // Treat as ready when: Flipt initialized successfully, sync workaround fired,
  // or polling gave up (graceful degradation — use SSR seed / default values).
  const effectiveIsLoading = hasGivenUp ? false : syncFix > 0 ? (fliptCtx as unknown as Record<string, unknown>).isLoading !== false : isLoading;

  // SSR seed values: capture once at mount — after Flipt initializes,
  // WASM evaluation replaces these via useFliptBoolean/useFliptVariant defaults.
  const ssrFlagsRef = useRef(ssrFlags);
  const ssrVariantsRef = useRef(ssrVariants);
  const contextValue = useMemo(() => ({
    userEntityId,
    deviceEntityId,
    isReady: !effectiveIsLoading,
    ssrFlags: ssrFlagsRef.current,
    ssrVariants: ssrVariantsRef.current
  }), [userEntityId, deviceEntityId, effectiveIsLoading]);
  return <FeatureFlagContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="FeatureFlagBridge" data-sentry-source-file="FeatureFlagProvider.tsx">
      {children}
    </FeatureFlagContext.Provider>;
}